// src/views/Shared/ClientProfile.js

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { db } from '../../firebase';
import {
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  limit,
} from 'firebase/firestore';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';

const ClientProfile = () => {
  const { clientId } = useParams();
  const [profile, setProfile] = useState(null);
  const [editData, setEditData] = useState({});
  const [resolvedDocId, setResolvedDocId] = useState(null);
  const [status, setStatus] = useState(null);
  const [imageUpload, setImageUpload] = useState(null);
  const [imageURL, setImageURL] = useState('');

  // Load profile
  useEffect(() => {
    const loadProfile = async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      const candidate = clientId || user?.email?.toLowerCase();
      if (!candidate) return;

      const candidateId = decodeURIComponent(candidate);
      let docRef = doc(db, 'clients', candidateId);
      let docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        const normalizedEmail = candidateId.includes('@') ? candidateId.toLowerCase() : null;
        const searches = [];
        if (normalizedEmail) {
          searches.push(where('email', '==', normalizedEmail));
        }
        searches.push(where('uid', '==', candidateId));

        let found = null;
        for (const condition of searches) {
          const qSnap = await getDocs(query(collection(db, 'clients'), condition, limit(1)));
          if (!qSnap.empty) {
            found = qSnap.docs[0];
            break;
          }
        }

        if (!found) {
          if (user) {
            const placeholderRef = doc(db, 'clients', user.uid);
            await setDoc(
              placeholderRef,
              {
                uid: user.uid,
                email: user.email?.toLowerCase() || '',
                name: user.displayName || '',
                createdAt: serverTimestamp(),
              },
              { merge: true }
            );
            docSnap = await getDoc(placeholderRef);
            docRef = placeholderRef;
          } else {
            console.warn('❗ Perfil no encontrado');
            return;
          }
        } else {
          docSnap = found;
          docRef = doc(db, 'clients', found.id);
        }

      }

      const data = docSnap.data();

      setProfile(data);
      setEditData(data);
      setResolvedDocId(docSnap.id);
      setImageURL(data?.profileImageUrl || '');
    };

    loadProfile();
  }, [clientId]);

  const handleChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setImageUpload(e.target.files[0]);
    }
  };

  const handleImageUpload = async () => {
    if (!imageUpload || !resolvedDocId) return;

    const storage = getStorage();
    const imgRef = storageRef(storage, `clients/${resolvedDocId}/profile.jpg`);

    try {
      await uploadBytes(imgRef, imageUpload);
      const downloadURL = await getDownloadURL(imgRef);
      setImageURL(downloadURL);

      await updateDoc(doc(db, 'clients', resolvedDocId), {
        profileImageUrl: downloadURL,
      });

      setStatus('success');
    } catch (err) {
      console.error('❌ Error subiendo imagen:', err);
      setStatus('error');
    }
  };

  const handleSave = async () => {
    if (!resolvedDocId) return;

    try {
      const ref = doc(db, 'clients', resolvedDocId);
      await updateDoc(ref, editData);
      setStatus('success');
    } catch (err) {
      console.error('❌ Error al guardar:', err);
      setStatus('error');
    }
  };

  if (!profile) return <p className="text-center text-gray-500 mt-10">Cargando perfil...</p>;

  return (
    <div className="max-w-3xl mx-auto mt-10 bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
      <h2 className="text-2xl font-semibold text-gray-800 dark:text-white mb-6">Perfil del Cliente</h2>

      {/* Estado */}
      {status === 'success' && (
        <div className="mb-4 text-green-700 bg-green-100 px-4 py-2 rounded-lg">
          ✅ Perfil actualizado correctamente
        </div>
      )}
      {status === 'error' && (
        <div className="mb-4 text-red-700 bg-red-100 px-4 py-2 rounded-lg">
          ❌ Hubo un error al guardar
        </div>
      )}

      {/* Imagen de perfil */}
      <div className="mb-6 text-center">
        <img
          src={imageURL || '/default-avatar.png'}
          alt="Imagen de perfil"
          className="mx-auto w-32 h-32 rounded-full object-cover border-4 border-gray-300 dark:border-gray-600"
        />
        <div className="mt-3">
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="block w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 cursor-pointer focus:outline-none dark:text-gray-400 dark:bg-gray-700 dark:border-gray-600"
          />
          <button
            onClick={handleImageUpload}
            className="mt-2 inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 dark:bg-blue-500 dark:hover:bg-blue-600 focus:outline-none dark:focus:ring-blue-800"
          >
            Subir Imagen
          </button>
        </div>
      </div>

      {/* Campos del perfil */}
      <form className="space-y-5">
        <div>
          <label htmlFor="businessName" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
            Nombre del Negocio
          </label>
          <input
            type="text"
            name="businessName"
            id="businessName"
            value={editData.businessName || ''}
            onChange={handleChange}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
          />
        </div>

        <div>
          <label htmlFor="phone" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
            Teléfono
          </label>
          <input
            type="text"
            name="phone"
            id="phone"
            value={editData.phone || ''}
            onChange={handleChange}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
          />
        </div>

        <div>
          <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
            Correo electrónico
          </label>
          <input
            type="email"
            name="email"
            id="email"
            value={editData.email || ''}
            onChange={handleChange}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
          />
        </div>

        <div>
          <label htmlFor="address" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
            Dirección
          </label>
          <input
            type="text"
            name="address"
            id="address"
            value={editData.address || ''}
            onChange={handleChange}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
          />
        </div>

        <button
          type="button"
          onClick={handleSave}
          className="w-full mt-4 text-white bg-green-600 hover:bg-green-700 focus:ring-4 focus:outline-none focus:ring-green-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-green-500 dark:hover:bg-green-600 dark:focus:ring-green-800"
        >
          Guardar Cambios
        </button>
      </form>
    </div>
  );
};

export default ClientProfile;
